import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { useChat } from './ChatProvider';
import { Button } from '../components/button';

// Gradient 'L' monogram icon for Lumi
function LumiIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="lumi-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="currentColor" stopOpacity="1" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <path 
        d="M7 4v16h10v-3H10V4H7z" 
        fill="url(#lumi-gradient)"
      />
    </svg>
  );
}

interface ChatButtonProps {
  className?: string;
}

export function ChatButton({ className }: ChatButtonProps) {
  const { isOpen, toggle, messages } = useChat();
  const hasMessages = messages.length > 0;
  
  // Show attention animation only once on first load
  const [showAttention, setShowAttention] = useState(false);
  
  useEffect(() => {
    // Delay the attention animation to let the page settle
    const timer = setTimeout(() => {
      if (!hasMessages) {
        setShowAttention(true);
        // Remove attention animation after it plays
        setTimeout(() => setShowAttention(false), 3000);
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  if (isOpen) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Subtle ripple ring - adjusted for pill shape */}
      {showAttention && !isOpen && (
        <span 
          className="absolute inset-0 rounded-full animate-ping bg-primary/30"
          style={{ animationDuration: '1.5s', animationIterationCount: '2' }}
        />
      )}
      
      {/* Subtle glow effect - always present when closed */}
      {!isOpen && (
        <span 
          className="absolute inset-0 rounded-full bg-primary/20 blur-md scale-105"
          aria-hidden="true"
        />
      )}
      
      <Button
        onClick={toggle}
        variant="default"
        className={cn(
          'relative h-10 md:h-12 px-4 md:px-5 gap-2 md:gap-3 rounded-full shadow-lg overflow-hidden',
          'transition-all duration-300 ease-out',
          'hover:scale-105 hover:shadow-xl active:scale-95',
          'bg-linear-to-br from-primary to-accent text-primary-foreground',
          'border border-white/10 backdrop-blur-xs',
          className
        )}
        aria-label="Chat with Lumi"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2 md:gap-3">
          <div className="bg-white/20 p-1 rounded-md">
            <LumiIcon className="h-4 w-4 md:h-5 md:w-5 text-white" />
          </div>
          <span className="text-sm md:text-base font-medium tracking-tight whitespace-nowrap">Chat with Lumi</span>
          {/* Online indicator dot */}
          <span 
            className="h-2 w-2 md:h-2.5 md:w-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]"
            style={{ animation: 'lumi-breathe 3s ease-in-out infinite' }}
            aria-hidden="true"
          />
        </div>
      </Button>
      
      {/* Breathing animation for online indicator */}
      <style>{`
        @keyframes lumi-breathe {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}

export default ChatButton;

